import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tag } from 'antd';
import { useSelector } from 'react-redux';
import { getEssaisRetours } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker, NdoeStatusBadge,
  NdoeResponsiveTable, NdoeImage, NdoeStats, NdoeStatCard,
  NdoeError, NdoeLoading, useIsDesktop,
  NdoeArticleLink, useFicheArticle,
} from '../../components/ndoe';
import { pct } from './format';
import './rapports.css';

// Barre de taux de garde : vert quand ça convertit, rouge quand ça revient.
const GardeBar = ({ value }) => {
  if (value == null) return <span className="num">—</span>;
  const tone = value >= 60 ? 'ok' : value >= 40 ? 'mid' : 'ko';
  return (
    <div className="er-garde" title={`${value} % des paires essayées sont gardées`}>
      <div className="er-garde__track"><i className={`is-${tone}`} style={{ width: `${Math.max(2, Math.min(100, value))}%` }} /></div>
      <span className="num">{value} %</span>
    </div>
  );
};

const STATUT_FR = {
  LIVREE: 'Livrées',
  ECHANGE_TERMINE: 'Échanges terminés',
  RETOUR_LIVRAISON: 'Retours livraison',
  RETOUR_CLIENT: 'Retours client',
  ECHANGE_RETOURNE: 'Échanges retournés',
  REPORTEE: 'Reportées',
  EN_ESSAI: "À l'essai",
  RETOUR_POUR_ECHANGE: 'Retours pour échange',
  ECHANGE_A_LIVRER: 'Échanges à livrer',
  ECHANGE_EN_COURS: 'Échanges en cours',
  ECHANGE_ANNULE: 'Échanges annulés',
};
const FAMILLE_COULEUR = { succes: 'green', echec: 'volcano', en_cours: 'default' };

export default function EssaisRetours() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  // Le nom et la référence d'une paire mènent à sa fiche — c'était déjà le cas
  // sur 6 rapports, pas ici (signalé le 31/07/2026).
  const { openFiche, ficheElement } = useFicheArticle();

  const [period, setPeriod] = useState({ period: 90 });
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Pagination contrôlée (desktop ET mobile — PagerMobile exige current/total).
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;
  const isDesktop = useIsDesktop();
  useEffect(() => { setPage(1); }, [period]);

  const apiParams = useMemo(() => ({
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
  }), [period]);

  const reqSeq = useRef(0);
  const load = useCallback(async () => {
    if (period.period === 'custom' && !(period.start_date && period.end_date)) { setLoading(false); return; }
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const { data } = await getEssaisRetours(apiParams);
      if (seq !== reqSeq.current) return;
      setPayload(data);
      setError('');
    } catch (e) {
      if (seq !== reqSeq.current) return;
      setError(e.response?.data?.error || 'Impossible de charger le rapport essais & retours.');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [apiParams, period]);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const s = payload?.summary || {};
  const liv = payload?.livraisons || {};
  const seuils = payload?.seuils || { alerte_taux: 40, alerte_volume: 5 };

  const columns = [
    { title: 'Modèle', key: 'p', render: (t, r) => (
      <div className="er-produit">
        <NdoeImage src={r.img} alt={r.nom_produit || 'Modèle'} size="sm" />
        <div className="er-produit__txt">
          {/* Le nom ET la référence mènent à la fiche (voir PertesEtZones). */}
          <NdoeArticleLink onClick={() => openFiche(r.id_produit)}>{r.nom_produit}</NdoeArticleLink>
          <small>
            {r.code_produit && (
              <NdoeArticleLink onClick={() => openFiche(r.id_produit)}>{r.code_produit}</NdoeArticleLink>
            )}
            {r.code_produit && r.marque ? ' · ' : ''}{r.marque || ''}
          </small>
        </div>
      </div>
    ) },
    { title: 'Gardées', dataIndex: 'gardees', key: 'g', align: 'right', render: (v) => <b className="num">{v}</b> },
    { title: 'Rendues', dataIndex: 'rendues', key: 'r', align: 'right', render: (v) => <b className="num er-rendues">{v}</b> },
    { title: 'Essais', dataIndex: 'essais', key: 'e', align: 'right', render: (v) => <span className="num">{v}</span> },
    { title: 'Taux de garde', key: 'tg', render: (t, r) => <GardeBar value={r.taux_garde} /> },
    { title: 'Pointures les plus rendues', key: 'pr', render: (t, r) => (
      <div className="er-chips">
        {(r.tailles_rendues || []).map((x, i) => <Tag key={i} color="volcano">P{x.taille} · {x.rendues}</Tag>)}
      </div>
    ) },
    { title: 'Signal', key: 'al', render: (t, r) => (
      r.alerte
        ? <NdoeStatusBadge kind="custom" tone="danger" label="À corriger / écarter" />
        : (r.taux_garde != null && r.taux_garde >= 60 && r.essais >= seuils.alerte_volume
          ? <NdoeStatusBadge kind="custom" tone="ok" label="Convertit bien" />
          : <span className="num">—</span>)
    ) },
  ];

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoePageHeader eyebrow="Rapports" title="Essais & retours par modèle" />
        <NdoeError title="Accès réservé" text="Ce rapport est réservé à l'administration." />
      </NdoePage>
    );
  }

  const guardePerso = period.period === 'custom' && !(period.start_date && period.end_date);

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Essais & retours par modèle"
        subtitle="Vos livraisons réussissent-elles, et quels modèles reviennent sans cesse de l'essai — pour corriger (taille, qualité, prix) ou écarter."
      />

      <div className="er-toolbar">
        <NdoePeriodPicker value={period} onChange={setPeriod} />
      </div>

      {/* « Perso » sans dates : PRIORITAIRE — on n'affiche ni les données de
          l'ancienne période (elles passeraient pour du Perso), ni une erreur
          périmée sans issue. */}
      {guardePerso ? (
        <p className="er-note">Choisissez les deux dates de la période « Perso » pour charger le rapport.</p>
      ) : error ? (
        <NdoeError title="Erreur" text={error} onRetry={load} />
      ) : loading || !payload ? (
        <NdoeLoading rows={5} />
      ) : (
        <>
          <NdoeStats>
            <NdoeStatCard label="Réussite des livraisons" value={liv.taux_reussite == null ? '—' : pct(liv.taux_reussite)} gold />
            <NdoeStatCard label="Taux de garde global (essais)" value={s.taux_garde_global == null ? '—' : pct(s.taux_garde_global)} />
            <NdoeStatCard label="Paires rendues" value={s.rendues ?? 0} />
            <NdoeStatCard label="Modèles en alerte" value={s.modeles_en_alerte ?? 0} />
          </NdoeStats>

          {(liv.par_statut || []).length > 0 && (
            <p className="er-hint">
              Livraisons tranchées : {liv.resolues} ({liv.succes} réussies · {liv.echecs} retours)
              {liv.en_cours > 0 && <> · en cours : {liv.en_cours}</>} —{' '}
              {(liv.par_statut || []).map((x, i) => (
                <Tag key={i} color={FAMILLE_COULEUR[x.famille] || 'default'}>
                  {STATUT_FR[x.statut] || x.statut} : {x.lignes}
                </Tag>
              ))}
              <small>Le taux ne compte que les issues tranchées — à l'essai, reportées et échanges en cours n'y pèsent pas ; le backlog jamais clôturé est hors périmètre.</small>
            </p>
          )}

          <div className="ndoe-card er-block">
            <NdoeResponsiveTable
              data={isDesktop ? (payload.data || []) : (payload.data || []).slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
              columns={columns}
              rowKey="id_produit"
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total: (payload.data || []).length,
                onChange: (p) => setPage(p),
                showTotal: (t) => `${t} modèle(s)`,
              }}
              scroll={{ x: 'max-content' }}
              empty={{ icon: '📦', title: 'Aucun essai sur la période' }}
            />
          </div>

          <p className="er-note">
            <b>Taux de garde</b> = paires gardées ÷ (gardées + rendues à l'essai) sur la période.
            <b> Alerte</b> = moins de {seuils.alerte_taux} % de garde sur au moins {seuils.alerte_volume} essais :
            le modèle plaît en photo mais ne reste pas aux pieds — taille qui chausse mal, qualité perçue ou prix.
            Les « rendues » comptent les DEUX circuits : retours enregistrés au bureau ET paires
            rendues via le hub livreur (présentées et non gardées) — chaque paire une seule fois.
            Les pointures les plus rendues pointent souvent un problème de taillant.
          </p>
        </>
      )}
      {/* Le tiroir de la fiche paire, monte une fois pour la page. */}
      {ficheElement}
    </NdoePage>
  );
}
